import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IconChevronDown,
  IconCopy,
  IconExternalLink,
  IconMaximize,
  IconPlus,
  IconRotate,
  IconSearch,
} from "@tabler/icons-react";
import {
  useEncoderAhaByIds,
  useEncoderAhaKeywordSearches,
  useEncoderPcsCodeDescription,
  useEncoderPcsDefinitionLookup,
  useEncoderPcsDefinitionsSearch,
  useEncoderPcsIndexSearch,
  useEncoderPcsTables,
  useEncoderPcsYears,
} from "../../hooks/queries";
import type {
  AhaGuideline,
  PcsDefinition,
  PcsIndexSearchResult,
} from "../../types/encoder";
import {
  cn,
  copyPlainText,
  encoderErrorMessage,
  formatAhaGuidelineTitle,
  formatAhaYearQuarterPage,
  getAhaExpandedQaContent,
} from "../../utils/encoder";
import {
  applyPcsFilters,
  buildCodeString,
  buildDescriptionPath,
  buildSelectedLabelsPath,
  collectPcsAhaKeywords,
  emptySelection,
  fillSolePcsOptions,
  getGuidelinesForOperation,
  getPcsAxisTitles,
  isPcsCharValidAtPosition,
  isPcsInspectorDefinitionAxis,
  parseGuidelineLine,
  parsePcsCodeInput,
  pcsSelectionsEqual,
  pruneIncompatiblePcsSelection,
  rankAhaResultsByPcsKeywords,
  selectionFromDefinition,
  selectionFromTargetCode,
  type PcsSelection,
} from "../../utils/encoder-pcs-filter";
import { useEncoderUi } from "./context";
import {
  EncoderDialog,
  PcsCodeBoxes,
  Spinner,
  useDebouncedValue,
} from "./shared";

const PcsInspectorDefinition: React.FC<{
  fiscalYear: number;
  section: string | null;
  pos: number;
  axisTitle: string;
  label: string;
}> = ({ fiscalYear, section, pos, axisTitle, label }) => {
  const lookup = useEncoderPcsDefinitionLookup(
    fiscalYear,
    section,
    label ? pos : null,
    label || null
  );
  if (!label) return null;
  const def = lookup.data;
  return (
    <div className="rounded-md border border-slate-200 px-3 py-2.5">
      <p className="font-semibold text-green-700">
        {axisTitle}: {label}
      </p>
      {lookup.isFetching && !def ? (
        <p className="mt-2 text-slate-500">Loading definition…</p>
      ) : def?.definition ? (
        <>
          <p className="mt-2 font-semibold text-slate-900">Definition:</p>
          <p className="mt-0.5 leading-relaxed text-slate-800">{def.definition}</p>
        </>
      ) : null}
      {def?.explanation ? (
        <>
          <p className="mt-2 font-semibold text-slate-900">Explanation:</p>
          <p className="mt-0.5 leading-relaxed text-slate-800">{def.explanation}</p>
        </>
      ) : null}
    </div>
  );
};

const PcsGuidelineList: React.FC<{ lines: string[] }> = ({ lines }) => (
  <ol className="space-y-2.5 p-3">
    {lines.map((line: string, index: number) => {
      const parsed = parseGuidelineLine(line);
      return (
        <li
          key={`${index}-${line.slice(0, 24)}`}
          className="flex gap-2 text-sm leading-relaxed text-amber-900"
        >
          <span className="w-4 shrink-0 text-right font-semibold text-amber-900">
            {index + 1}.
          </span>
          <p className="min-w-0 flex-1">
            {parsed.label ? (
              <span className="font-semibold">{parsed.label}: </span>
            ) : null}
            {parsed.text}
          </p>
        </li>
      );
    })}
  </ol>
);

const PcsAhaList: React.FC<{
  rows: AhaGuideline[];
  loading: boolean;
  openIds: number[];
  onToggle: (id: number) => void;
  detailsById: Record<number, AhaGuideline>;
  detailsLoading: boolean;
}> = ({ rows, loading, openIds, onToggle, detailsById, detailsLoading }) => {
  if (loading && rows.length === 0) {
    return <Spinner label="Looking up AHA Coding Clinic…" />;
  }
  if (rows.length === 0) {
    return (
      <p className="px-4 py-6 text-sm text-slate-600">
        No AHA Coding Clinic reference matches the selected options.
      </p>
    );
  }
  return (
    <ul className="divide-y">
      {rows.map((row: AhaGuideline, index: number) => {
        const isOpen = openIds.indexOf(row.id) >= 0;
        const detail = detailsById[row.id] ?? row;
        const { qaPairs, fallbackText } = getAhaExpandedQaContent(detail);
        const meta = formatAhaYearQuarterPage(row);
        return (
          <li key={row.id} className={cn(isOpen && "bg-slate-100/60")}>
            <button
              type="button"
              onClick={() => onToggle(row.id)}
              className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-slate-100/50"
            >
              <span className="mt-px w-4 shrink-0 text-right text-xs text-slate-600">
                {index + 1}.
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold leading-5 text-slate-900">
                  {formatAhaGuidelineTitle(row)}
                </span>
                {meta ? (
                  <span className="mt-0.5 block text-xs text-slate-600">
                    {meta}
                  </span>
                ) : null}
              </span>
              <IconChevronDown
                size={16}
                className={cn(
                  "mt-0.5 shrink-0 text-slate-500 transition-transform",
                  isOpen && "rotate-180"
                )}
              />
            </button>
            {isOpen ? (
              <div className="mx-3 mb-2 space-y-2 border-l-2 border-l-slate-400 bg-white/60 px-3 py-2 text-xs text-slate-800">
                {detailsLoading &&
                !detailsById[row.id] &&
                !fallbackText &&
                qaPairs.length === 0 ? (
                  <p className="text-slate-600">Loading clinic text…</p>
                ) : (
                  <>
                    {qaPairs.map(
                      (
                        pair: { question: string; answer: string },
                        pairIndex: number
                      ) => (
                        <div
                          key={`${row.id}-qa-${pairIndex}`}
                          className="space-y-2"
                        >
                          {pair.question ? (
                            <p>
                              <span className="font-semibold text-red-800">
                                Question:{" "}
                              </span>
                              {pair.question}
                            </p>
                          ) : null}
                          {pair.answer ? (
                            <p>
                              <span className="font-semibold text-green-800">
                                Answer:{" "}
                              </span>
                              {pair.answer}
                            </p>
                          ) : null}
                        </div>
                      )
                    )}
                    {!qaPairs.length && fallbackText ? (
                      <p className="whitespace-pre-line">{fallbackText}</p>
                    ) : null}
                    {!qaPairs.length && !fallbackText ? (
                      <p className="text-slate-600">
                        No AHA Coding Clinic text is stored for this entry.
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

const PcsBuilderTab: React.FC<{
  onConfirmCode?: (payload: { code: string; description: string }) => void;
}> = ({ onConfirmCode }) => {
  const { showToast } = useEncoderUi();
  const yearsQuery = useEncoderPcsYears();
  const years: number[] = yearsQuery.data?.years?.length
    ? yearsQuery.data.years
    : [2026, 2025, 2024];
  const [fiscalYear, setFiscalYear] = useState(
    yearsQuery.data?.defaultYear ?? years[0] ?? 2026
  );
  const [selection, setSelection] = useState<PcsSelection>(emptySelection);
  const [alphaQuery, setAlphaQuery] = useState("");
  const debouncedQuery = useDebouncedValue(alphaQuery, 300);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [guidelinesTab, setGuidelinesTab] = useState<"coding" | "aha">("coding");
  const [guidelinesExpanded, setGuidelinesExpanded] = useState(false);
  const [openAhaIds, setOpenAhaIds] = useState<number[]>([]);
  const searchWrapRef = useRef<HTMLDivElement>(null);

  const tablesQuery = useEncoderPcsTables(fiscalYear);
  const indexQuery = useEncoderPcsIndexSearch(fiscalYear, debouncedQuery);
  const defsQuery = useEncoderPcsDefinitionsSearch(fiscalYear, debouncedQuery);

  const rows = useMemo(
    () => tablesQuery.data?.rows ?? [],
    [tablesQuery.data?.rows]
  );
  const filterResult = useMemo(
    () => applyPcsFilters(rows, selection),
    [rows, selection]
  );

  useEffect(() => {
    if (!rows.length) return;
    setSelection((prev) => {
      const pruned = pruneIncompatiblePcsSelection(rows, prev);
      const next = fillSolePcsOptions(rows, pruned);
      return pcsSelectionsEqual(prev, next) ? prev : next;
    });
  }, [filterResult.available, rows]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (
        searchWrapRef.current &&
        !searchWrapRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const codeString = buildCodeString(selection);
  const isComplete = ![1, 2, 3, 4, 5, 6, 7].some((pos) => !selection[pos]);
  const descriptionPath = buildDescriptionPath(selection, filterResult.labels);
  const rootPath = buildSelectedLabelsPath(selection, filterResult.labels);
  const sectionChar = selection[1];
  const headers = useMemo(
    () => getPcsAxisTitles(sectionChar),
    [sectionChar]
  );
  const codeDescriptionQuery = useEncoderPcsCodeDescription(
    fiscalYear,
    isComplete ? codeString : null
  );
  const operationLabel = selection[3]
    ? filterResult.labels[3]?.[selection[3]] ?? ""
    : "";
  const operationDef = useEncoderPcsDefinitionLookup(
    fiscalYear,
    selection[1],
    selection[3] ? 3 : null,
    operationLabel || null
  );
  const guidelines = useMemo(
    () =>
      getGuidelinesForOperation(
        operationLabel,
        operationDef.data
          ? {
              title: operationDef.data.title,
              definition: operationDef.data.definition,
              explanation: operationDef.data.explanation,
            }
          : null
      ),
    [operationLabel, operationDef.data]
  );
  const officialDescription = isComplete
    ? codeDescriptionQuery.data || descriptionPath
    : "";
  const ahaKeywords = useMemo(
    () =>
      collectPcsAhaKeywords(
        selection,
        filterResult.labels,
        officialDescription ? [officialDescription] : []
      ),
    [filterResult.labels, officialDescription, selection]
  );
  const ahaSearch = useEncoderAhaKeywordSearches(ahaKeywords);
  const ahaRows = useMemo((): AhaGuideline[] => {
    const rows: AhaGuideline[] = ahaSearch.data ?? [];
    return rankAhaResultsByPcsKeywords(rows, ahaKeywords);
  }, [ahaSearch.data, ahaKeywords]);
  const ahaKeywordKey = ahaKeywords.join("|");
  const hasSelection = Boolean(selection[1]);
  const ahaDetails = useEncoderAhaByIds(
    openAhaIds,
    guidelinesTab === "aha" && openAhaIds.length > 0
  );
  const ahaById = useMemo(() => {
    const map: Record<number, AhaGuideline> = {};
    const details = ahaDetails.data ?? [];
    for (let i = 0; i < details.length; i += 1) map[details[i].id] = details[i];
    return map;
  }, [ahaDetails.data]);
  const pdfUrl =
    tablesQuery.data?.pdfUrl ??
    yearsQuery.data?.pdfByYear?.[String(fiscalYear)] ??
    null;

  useEffect(() => {
    setOpenAhaIds([]);
  }, [ahaKeywordKey]);

  const applySelection = (next: PcsSelection) => {
    setSelection(next);
  };

  const handleTypedChar = (pos: number, char: string | null): boolean => {
    if (char && rows.length && !isPcsCharValidAtPosition(rows, selection, pos, char)) {
      showToast(
        `'${char}' is not valid for character ${pos} with the current code`,
        "error"
      );
      return false;
    }
    applySelection({ ...selection, [pos]: char });
    return true;
  };

  const applyPastedCode = (raw: string) => {
    const parsed = parsePcsCodeInput(raw);
    if (!parsed) {
      showToast("Paste a valid ICD-10-PCS code", "error");
      return;
    }
    applySelection(
      pruneIncompatiblePcsSelection(rows, selectionFromTargetCode(parsed))
    );
  };

  const handleIndexSelect = (entry: PcsIndexSearchResult) => {
    setShowSuggestions(false);
    setAlphaQuery(entry.display);
    const target = entry.targetCode || entry.code || entry.tab || null;
    if (!target) return;
    applySelection(selectionFromTargetCode(target));
  };

  const handleDefinitionSelect = (def: PcsDefinition) => {
    applySelection(selectionFromDefinition(rows, def));
    setShowSuggestions(false);
  };

  const handleCopy = async () => {
    if (!isComplete) return;
    const copied = await copyPlainText(codeString);
    showToast(
      copied
        ? "Code copied to clipboard"
        : "Unable to copy in this window. Select the code boxes and copy manually.",
      copied ? "success" : "error"
    );
  };

  const toggleAha = (id: number) => {
    setOpenAhaIds((current) =>
      current.indexOf(id) >= 0
        ? current.filter((item) => item !== id)
        : current.concat([id])
    );
  };

  if (tablesQuery.isPending && !tablesQuery.data) {
    return <Spinner label="Loading ICD-10-PCS tables…" />;
  }

  if (tablesQuery.isError) {
    return (
      <p className="p-6 text-sm text-red-600">
        {encoderErrorMessage(tablesQuery.error, "Failed to load PCS explorer data.")}
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-slate-50">
      <div className="shrink-0 border-b bg-white px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm font-semibold text-green-700">
            ICD-10-PCS Code Explorer
          </p>
          <select
            value={String(fiscalYear)}
            onChange={(event) => {
              setFiscalYear(Number(event.target.value));
              setSelection(emptySelection());
              setAlphaQuery("");
            }}
            className="h-8 rounded-md border border-slate-300 bg-slate-50 px-2 text-sm"
          >
            {years.map((year: number) => (
              <option key={year} value={year}>
                FY {year}
              </option>
            ))}
          </select>
          <div className="relative min-w-[16rem] flex-1" ref={searchWrapRef}>
            <IconSearch
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={alphaQuery}
              onChange={(event) => {
                setAlphaQuery(event.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Alpha Index Search"
              className="h-8 w-full rounded-md border border-slate-200 pl-8 pr-3 text-sm"
            />
            {showSuggestions && debouncedQuery && (
              <div className="absolute z-40 mt-1 max-h-96 w-[min(40rem,calc(100vw-2rem))] overflow-auto rounded-md border bg-white shadow-lg">
                {defsQuery.isFetching || indexQuery.isFetching ? (
                  <Spinner />
                ) : (
                  <>
                    {(defsQuery.data ?? []).slice(0, 12).map((def: PcsDefinition) => (
                      <button
                        key={`def-${def.section}-${def.pos}-${def.title}`}
                        type="button"
                        className="block w-full border-b px-3 py-2 text-left text-sm hover:bg-green-50"
                        onClick={() => handleDefinitionSelect(def)}
                      >
                        <span className="font-semibold">{def.title}</span>
                        <span className="text-xs text-slate-500">
                          {" "}
                          (Sec {def.section}, Pos {def.pos})
                        </span>
                      </button>
                    ))}
                    {(indexQuery.data ?? []).slice(0, 20).map(
                      (entry: PcsIndexSearchResult, index: number) => (
                        <button
                          key={`${entry.display}-${index}`}
                          type="button"
                          className="block w-full border-b px-3 py-2 text-left text-sm hover:bg-green-50"
                          onClick={() => handleIndexSelect(entry)}
                        >
                          {entry.display}
                          {entry.code ? (
                            <span className="ml-2 font-mono text-xs text-blue-800">
                              {entry.code}
                            </span>
                          ) : null}
                        </button>
                      )
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-3">
            <span className="whitespace-nowrap text-xs font-semibold text-slate-500">
              Current Code:
            </span>
            <PcsCodeBoxes
              selection={selection}
              onApplyCode={applyPastedCode}
              onChangeChar={handleTypedChar}
            />
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1.5 rounded-md border px-2.5 text-sm"
              onClick={() => void handleCopy()}
              disabled={!isComplete}
            >
              <IconCopy size={14} /> Copy Code
            </button>
            {onConfirmCode && (
              <button
                type="button"
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-green-600 px-2.5 text-sm font-semibold text-white"
                disabled={!isComplete}
                onClick={() =>
                  onConfirmCode({
                    code: codeString,
                    description: codeDescriptionQuery.data ?? descriptionPath,
                  })
                }
              >
                <IconPlus size={14} /> Add Code
              </button>
            )}
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center gap-1.5 rounded-md border px-2.5 text-sm hover:bg-green-50 hover:text-green-800"
              >
                <IconExternalLink size={14} /> FY {fiscalYear} PDF
              </a>
            )}
            {onConfirmCode ? (
              <button
                type="button"
                className="ml-auto inline-flex h-8 items-center gap-1 rounded-md bg-red-600 px-3 text-sm text-white"
                onClick={() => {
                  setSelection(emptySelection());
                  setAlphaQuery("");
                }}
              >
                <IconRotate size={14} /> Reset
              </button>
            ) : null}
          </div>
        </div>
        <p className="mt-1.5 truncate text-base font-medium italic text-green-700">
          {isComplete
            ? codeDescriptionQuery.data || descriptionPath
            : selection[1]
              ? descriptionPath
              : ""}
        </p>
      </div>

      <div className="flex min-h-0 flex-1 gap-2 p-2">
        <div className="grid min-h-0 min-w-0 flex-1 grid-cols-7 gap-1.5">
          {headers.map((header, index) => {
            const pos = index + 1;
            const options = filterResult.available[pos] ?? [];
            return (
              <div
                key={header}
                className="flex min-h-0 flex-col overflow-hidden rounded-md border bg-white"
              >
                <div className="shrink-0 border-b bg-slate-100 px-2 py-1.5 text-xs font-semibold">
                  {pos}. {header}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-1">
                  {options.map((code) => {
                    const selected = selection[pos] === code;
                    const label = filterResult.labels[pos]?.[code] ?? "";
                    return (
                      <button
                        key={`${pos}-${code}`}
                        type="button"
                        onClick={() =>
                          applySelection({
                            ...selection,
                            [pos]: selected ? null : code,
                          })
                        }
                        className={cn(
                          "mb-0.5 w-full rounded px-1.5 py-1 text-left text-xs leading-snug",
                          selected ? "bg-green-600 text-white" : "hover:bg-green-100"
                        )}
                      >
                        <span className="font-semibold">{code}</span>
                        <span className={selected ? "text-white/90" : "text-slate-500"}>
                          {" "}
                          - {label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <aside className="flex w-[400px] shrink-0 flex-col overflow-hidden rounded-md border bg-white">
          <div className="shrink-0 border-b bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-green-700">
            Clinical Inspector
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-2.5 text-xs leading-relaxed">
            {!hasSelection ? (
              <>
                <p className="font-mono text-sm font-bold italic text-green-700">
                  ICD-10-PCS: {codeString}
                </p>
                <p>
                  <span className="font-semibold text-green-700">DESCRIPTION:</span>{" "}
                  {descriptionPath}
                </p>
                <p className="text-slate-500">
                  Select characters or search the alpha index to build a code.
                </p>
              </>
            ) : (
              <>
                <p>
                  <span className="font-semibold text-green-700">ROOT:</span>{" "}
                  {rootPath || "—"}
                </p>
                <p>
                  <span className="font-semibold text-green-700">DESCRIPTION:</span>{" "}
                  {isComplete
                    ? codeDescriptionQuery.isFetching && !officialDescription
                      ? "Loading…"
                      : officialDescription || "- - - - - - -"
                    : "- - - - - - -"}
                </p>
                {headers.map((axisTitle: string, index: number) => {
                  const pos = index + 1;
                  const code = selection[pos];
                  if (!code || !isPcsInspectorDefinitionAxis(axisTitle)) {
                    return null;
                  }
                  const label = filterResult.labels[pos]?.[code] ?? "";
                  return (
                    <PcsInspectorDefinition
                      key={`${pos}-${code}-${label}`}
                      fiscalYear={fiscalYear}
                      section={sectionChar}
                      pos={pos}
                      axisTitle={axisTitle}
                      label={label}
                    />
                  );
                })}
              </>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1.5 border-t bg-slate-100 px-2 py-1">
            <button
              type="button"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
              aria-label={
                guidelinesTab === "aha"
                  ? "Expand AHA Coding Clinic"
                  : "Expand coding guidelines"
              }
              title="Expand"
              onClick={() => setGuidelinesExpanded(true)}
            >
              <IconMaximize size={14} />
            </button>
            {(["coding", "aha"] as const).map((tab) => {
              const count = tab === "coding" ? guidelines.length : ahaRows.length;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setGuidelinesTab(tab)}
                  className={cn(
                    "inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs",
                    guidelinesTab === tab
                      ? "bg-green-600 font-semibold text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-green-100"
                  )}
                >
                  {tab === "coding" ? "Coding Guidelines" : "AHA Coding Clinic"}
                  {count > 0 ? (
                    <span className="rounded-full bg-blue-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                      {count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="h-[42%] min-h-[200px] overflow-auto">
            {guidelinesTab === "coding" ? (
              <PcsGuidelineList lines={guidelines} />
            ) : (
              <PcsAhaList
                rows={ahaRows}
                loading={ahaSearch.isFetching}
                openIds={openAhaIds}
                onToggle={toggleAha}
                detailsById={ahaById}
                detailsLoading={ahaDetails.isFetching}
              />
            )}
          </div>
        </aside>
      </div>

      <EncoderDialog
        open={guidelinesExpanded}
        title={
          guidelinesTab === "aha"
            ? "AHA Coding Clinic"
            : "ICD-10-PCS Guidelines"
        }
        onClose={() => setGuidelinesExpanded(false)}
        wide
      >
        {guidelinesTab === "coding" ? (
          <PcsGuidelineList lines={guidelines} />
        ) : (
          <PcsAhaList
            rows={ahaRows}
            loading={ahaSearch.isFetching}
            openIds={openAhaIds}
            onToggle={toggleAha}
            detailsById={ahaById}
            detailsLoading={ahaDetails.isFetching}
          />
        )}
      </EncoderDialog>
    </div>
  );
};

export default PcsBuilderTab;
